import { computed, inject, Injectable, type Signal } from '@angular/core';
import { select } from '@ngxs/store';

import type { UserInformation } from '@efficiently/client-api-auth';
import { CoreSelectors } from '@efficiently/client-domain-core';
import { ProjectDetailSelectors } from '@efficiently/client-domain-project-detail';

import { PlatformAdminTabsStore } from '@ds/pages/platform-admin/platform-admin-tabs.store';
import { ProjectNavigatorStore } from '@ds/pages/projects/navigator/project-navigator.store';
import { PLATFORM_ADMIN_LABEL } from '@ds/shared/constants/platform-admin-tabs.constant';
import { toUserDisplayName } from '@ds/shared/utils/user-display-name.util';
import { DomainPlaceStore } from '@ds/shell/domain-place/domain-place.store';
import { DOMAIN_PLACE_KIND } from '@ds/shell/domain-place/domain-place-kind.constant';
import { ProjectDomainLinkStore } from '@ds/shell/project-domain-link/project-domain-link.store';

const WORKING_DOMAIN_TITLE_SEPARATOR = ' - ';

/**
 * The one answer to "what is the header's title right now" — the Platform Administrator label inside
 * that group, the current project's name inside a project, the Clarity domain's name on a domain
 * (the organisation and the person on the working domain), and nothing anywhere else.
 *
 * **Why a service and not a computed on the header.** The sources sit on opposite sides of the
 * component tree from the header: {@link PlatformAdminTabsStore} is written from inside a lazily
 * loaded route subtree, the project's identity is split between the router (the navigator) and
 * NGXS (what is actually loaded), and the domain place is registered by the Clarity environment
 * below the shell. Collecting them here keeps the precedence in one readable place
 * instead of spreading it across a template, and gives phase 2's header a plain `Signal` to bind.
 *
 * `providedIn: 'root'` for the same reason the two stores carry it: every source it reads is a root
 * singleton, and a service provided on the header component would be rebuilt with the header.
 *
 * **It reports WHERE THE USER IS, never what the user MAY DO.** Nothing here is an authorization
 * signal — `isActive` in particular says the Platform Administrator group is open, not that the
 * session may do anything inside it. The `canMatch` permission guards on the routes remain the only
 * gate, and no caller may read this title as one.
 */
@Injectable({ providedIn: 'root' })
export class HeaderContextTitleService {
	private readonly platformAdminTabsStore = inject(PlatformAdminTabsStore);

	private readonly projectNavigatorStore = inject(ProjectNavigatorStore);

	private readonly domainPlaceStore = inject(DomainPlaceStore);

	private readonly projectDomainLink = inject(ProjectDomainLinkStore);

	/**
	 * The project NGXS currently holds, and its name.
	 *
	 * Initialized as FIELDS, which is the only correct place for them: `select()` injects the store
	 * implicitly, so it needs an injection context. Called from inside the `computed` below — or from
	 * a `setTimeout`, a `Promise.then` or module scope — it throws at runtime, and no compiler, lint
	 * rule or test in this repository catches it. The signals are read inside the `computed`; the
	 * `select()` calls stay here.
	 */
	private readonly loadedProjectId = select<string>(ProjectDetailSelectors.projectId);

	private readonly loadedProjectName = select<string>(ProjectDetailSelectors.projectName);

	private readonly userInfo = select<UserInformation>(CoreSelectors.userInfo);

	/**
	 * The header's title, by first match: the admin label, then the active project's name, then the
	 * Clarity domain the URL is on, then nothing.
	 */
	public readonly title: Signal<string | null> = computed(() => {
		if (this.platformAdminTabsStore.isActive()) {
			return PLATFORM_ADMIN_LABEL;
		}

		const activeProjectId = this.projectNavigatorStore.activeProjectId();

		if (activeProjectId === null) {
			return this.domainPlaceTitle();
		}

		if (activeProjectId !== this.loadedProjectId()) {
			return this.linkedProjectName();
		}

		const loadedProjectName = this.loadedProjectName();

		if (loadedProjectName.length === 0) {
			return null;
		}

		return loadedProjectName;
	});

	private domainPlaceTitle(): string | null {
		const place = this.domainPlaceStore.place();

		if (place === null || place.kind === DOMAIN_PLACE_KIND.UNRESOLVED) {
			return null;
		}

		if (place.kind === DOMAIN_PLACE_KIND.PROJECT) {
			return this.linkedProjectName() ?? (place.name.length === 0 ? null : place.name);
		}

		const titleParts =
			place.kind === DOMAIN_PLACE_KIND.WORKING ? [place.name, toUserDisplayName(this.userInfo())] : [place.name];
		const nonEmptyParts = titleParts.filter(part => part.length > 0);

		return nonEmptyParts.length === 0 ? null : nonEmptyParts.join(WORKING_DOMAIN_TITLE_SEPARATOR);
	}

	private linkedProjectName(): string | null {
		const projectName = this.projectDomainLink.current()?.projectName ?? null;

		return projectName === null || projectName.length === 0 ? null : projectName;
	}
}
